import React, { useEffect, useMemo, useState } from "react";
import PermissionButton from "../shared/PermissionButton";
import Select from "../shared/Select";
import { useYearLevelByAssessmentSubjectConfig } from "../../hooks/useSubjectAssessment";
import {
  useSelectClassrooms,
  useStudents,
} from "../../hooks/useOptionalSubjects";
import {
  useGeneralAssessmentSelectByYearAndClass,
  useGeneralRecordedAssessment,
  useIsGeneralAssessmentFinalized,
  useResetGeneralAssessment,
} from "../../hooks/useGeneralAssessment";
import Button from "../shared/Button";
import { Spinner } from "reactstrap";
import CustomModal from "../shared/CustomModal";
import AssessmentConfigModal from "./AssessmentConfigModal";
import {
  checkRolePermissions,
  checkSubsTeacherPermissions,
} from "../../../../utils/Constants";
import { useARRCode } from "../../context/ARRLevelContext";
import { Tooltip } from "@mui/material";

const GeneralAssessment = ({ name }) => {
  const { arrLevel } = useARRCode();
  const { yearLevel, yearLoading } =
    useYearLevelByAssessmentSubjectConfig(name);
  const { classrooms, classroomLoading, getClassrooms } = useSelectClassrooms();
  const {
    assessments,
    assessmentsLoading,
    generalAssessmentSelectByYearAndClass,
  } = useGeneralAssessmentSelectByYearAndClass();
  const {
    students,
    setStudents,
    getAllStudents,
    loading: isFetchingStudents,
  } = useStudents();
  const {
    finalized,
    loading: isFinalizing,
    updateAssessmentFinalized,
    getAssessmentFinalized: refetchFinalized,
  } = useIsGeneralAssessmentFinalized();

  const {
    recordedAssessment,
    recordedAssessmentLoading,
    selectGeneralRecordedAssessment,
    saveGeneralRecordedAssessment,
  } = useGeneralRecordedAssessment(refetchFinalized);

  const { loading: reseting, resetGeneralAssessment } =
    useResetGeneralAssessment();

  const [formData, setFormData] = useState({});
  const [assessmentModal, setAssessmentModal] = useState(false);
  const [selectStudentId, setSelectedStudentId] = useState(null);
  const [permissions, setPermissions] = useState({
    finalizeGeneralPermission: false,
    unFinalizeGeneralPermission: false,
  });
  const [resetModal, setResetModal] = useState(false);

  const yearLevelOptions = useMemo(() => {
    if (yearLevel?.length === 0) {
      return [];
    }

    return yearLevel?.map((item) => ({
      educationLevelId: item?.educationLevelId,
      label: item.name,
      value: item?.id,
    }));
  }, [yearLevel]);

  const handleAssessmentChange = (option) => {
    setFormData((prev) => ({
      ...prev,
      assessmentId: option?.value,
      educationLevelDivisionId: option?.educationLevelDivisionId,
    }));

    const studentParams = {
      assessmentId: option?.value,
      moduleCode: arrLevel,
      classroomId: formData?.classroomId,
      yearLevelId: formData?.yearLevelId,
    };
    getAllStudents(studentParams);

    const finalizedParams = {
      assessmentId: option?.value,
      classroomId: formData?.classroomId,
      yearLevelId: formData?.yearLevelId,
    };

    refetchFinalized(finalizedParams);
  };

  const handleFinalizedAssessment = () => {
    const originalIsFinalized = finalized?.[0]?.isFinalized ?? false;

    if (
      !formData?.assessmentId ||
      !formData?.classroomId ||
      !formData?.yearLevelId
    )
      return;

    const params = {
      assessmentId: formData?.assessmentId,
      classroomId: formData?.classroomId,
      isFinalized: !originalIsFinalized,
      yearLevelId: formData?.yearLevelId,
    };

    updateAssessmentFinalized(params);
  };

  const checkPermissons = () => {
    setPermissions({
      finalizeGeneralPermission:
        checkRolePermissions("finalize-general-assessment", "activity", name) ||
        checkSubsTeacherPermissions(
          "finalize-general-assessment",
          "activity",
          name,
        ),
      unFinalizeGeneralPermission:
        checkRolePermissions(
          "unfinalize-general-assessment",
          "activity",
          name,
        ) ||
        checkSubsTeacherPermissions(
          "unfinalize-general-assessment",
          "activity",
          name,
        ),
    });
  };

  const handleResetRecords = async () => {
    const payload = {
      yearLevelId: formData?.yearLevelId,
      classroomId: formData?.classroomId,
      assessmentId: formData?.assessmentId,
    };

    try {
      await resetGeneralAssessment(payload);
      setResetModal(false);
      const studentParams = {
        assessmentId: formData?.assessmentId,
        moduleCode: arrLevel,
        classroomId: formData?.classroomId,
        yearLevelId: formData?.yearLevelId,
      };
      getAllStudents(studentParams);

      const finalizedParams = {
        assessmentId: formData?.assessmentId,
        classroomId: formData?.classroomId,
        yearLevelId: formData?.yearLevelId,
      };

      refetchFinalized(finalizedParams);
    } catch (error) {
      console.error("Reset Failed: ", error);
    }
  };

  useEffect(() => {
    checkPermissons();
    setFormData({});
    setStudents([]);
  }, [arrLevel]);

  return (
    <>
      <div className="arr-content">
        <div className="arr-heading">
          <h1>General Assessment</h1>
          <div className="arr-btns-group">
            <PermissionButton
              moduleName={"Recording"}
              widgetName={name}
              header={"General Assessment"}
              activityName={"select-general-assessment"}
            />
          </div>
        </div>

        <form style={{ marginBottom: "24px" }} className="arr-form grid">
          <Select
            value={
              yearLevelOptions?.find(
                (opt) => opt.value === formData.yearLevelId,
              ) || null
            }
            options={yearLevelOptions}
            onChange={(option) => {
              setFormData({
                yearLevelId: option?.value,
                educationLevelId: option?.educationLevelId,
              });
              setStudents([]);
              getClassrooms(option?.value);
            }}
            isLoading={yearLoading}
            placeholder={"Select Class"}
            isClearable={false}
          />

          <Select
            value={
              classrooms?.find((opt) => opt.value === formData.classroomId) ||
              null
            }
            options={classrooms}
            isLoading={classroomLoading}
            onChange={(option) => {
              setFormData((prev) => ({ ...prev, classroomId: option?.value }));
              setStudents([]);
              const params = {
                classroomId: option?.value,
                yearLevelId: formData?.yearLevelId,
                educationLevelId: formData?.yearLevelId,
              };
              generalAssessmentSelectByYearAndClass(params);
            }}
            isDisabled={!formData?.yearLevelId}
            placeholder={"Select Section"}
            isClearable={false}
          />

          <Select
            value={
              assessments?.find((opt) => opt.value === formData.assessmentId) ||
              null
            }
            options={assessments}
            onChange={(option) => handleAssessmentChange(option)}
            isLoading={assessmentsLoading}
            isDisabled={!formData?.classroomId}
            isClearable={false}
            placeholder={"Select Assessments"}
          />
        </form>

        <div className="arr-table-container">
          <table className="arr-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Name</th>
                <th>Roll Number</th>
                <th>Registration Number</th>
                <th>Symbol Number</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isFetchingStudents ? (
                <tr>
                  <td colSpan={6}>
                    <div className="arr-spinner">
                      <div className="loader">
                        <Spinner size={"md"} color="primary"></Spinner>
                      </div>
                      <div style={{ fontSize: "13px", marginTop: "15px" }}>
                        Please wait !! Processing.....
                      </div>
                    </div>
                  </td>
                </tr>
              ) : students?.length > 0 ? (
                students?.map((student, index) => (
                  <tr key={student.id}>
                    <td>{index + 1}</td>
                    <td>{student?.name}</td>
                    <td>{student?.rollNumber ?? "N/A"}</td>
                    <td>{student?.registrationNumber ?? "N/A"}</td>
                    <td>{student?.symbolNo ?? "N/A"}</td>
                    <td>
                      <Button
                        onClick={() => {
                          setAssessmentModal(true);
                          setSelectedStudentId(student?.id);
                          const params = {
                            ...formData,
                            admissionId: student?.id,
                          };
                          selectGeneralRecordedAssessment(params);
                        }}
                        size="sm"
                      >
                        Assessment Record
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center" }}>
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {students?.length > 0 && (
          <div className="action-btns end">
            <Tooltip
              title={
                finalized?.[0]?.isFinalized
                  ? "You cannot reset records because records are finalized"
                  : ""
              }
            >
              <div>
                <Button
                  disabled={finalized?.[0]?.isFinalized}
                  onClick={() => setResetModal(true)}
                  variant="danger"
                >
                  Reset All
                </Button>
              </div>
            </Tooltip>

            {permissions.finalizeGeneralPermission &&
              !finalized?.[0]?.isFinalized && (
                <Button
                  disabled={isFinalizing || !recordedAssessment?.hasPermission}
                  onClick={handleFinalizedAssessment}
                >
                  {isFinalizing ? (
                    <Spinner size={"sm"} color="white" />
                  ) : (
                    "Finalize Assessment"
                  )}
                </Button>
              )}

            {permissions.unFinalizeGeneralPermission &&
              finalized?.[0]?.isFinalized && (
                <Button
                  disabled={isFinalizing || !recordedAssessment?.hasPermission}
                  onClick={handleFinalizedAssessment}
                >
                  {isFinalizing ? (
                    <Spinner size={"sm"} color="white" />
                  ) : (
                    "Unfinalize Assessment"
                  )}
                </Button>
              )}
          </div>
        )}
      </div>

      <CustomModal
        isOpen={resetModal}
        toggle={() => setResetModal(false)}
        modalHeader={"Are you sure?"}
        children={
          <div>
            <p>Once reset, you will not be able to recover this record</p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "8px",
              }}
            >
              <Button variant="outline" onClick={() => setResetModal(false)}>
                Cancel
              </Button>
              <Button
                disabled={reseting}
                variant="danger"
                onClick={handleResetRecords}
              >
                {reseting ? <Spinner size={"sm"} color="white" /> : "Reset"}
              </Button>
            </div>
          </div>
        }
      />

      <CustomModal
        size="md"
        isOpen={assessmentModal}
        toggle={() => setAssessmentModal(false)}
        modalHeader={"General Assessment"}
        children={
          <AssessmentConfigModal
            assessmentDetail={formData}
            recordedAssessment={recordedAssessment}
            recordedAssessmentLoading={recordedAssessmentLoading}
            saveGeneralRecordedAssessment={saveGeneralRecordedAssessment}
            selectGeneralRecordedAssessment={selectGeneralRecordedAssessment}
            refetchFinalized={refetchFinalized}
            admissionId={selectStudentId}
            finalized={finalized?.[0]?.isFinalized ?? false}
            closeModal={() => setAssessmentModal(false)}
            widgetName={name}
          />
        }
      />
    </>
  );
};

export default GeneralAssessment;
